import { ITEMS } from '../core/defs';
import type { BuildingKind, ItemKind } from '../core/types';

type Ctx = CanvasRenderingContext2D;

export const INK = '#E8EEF5';
export const DIM = '#8FA0B3';

export function roundRect(ctx: Ctx, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function poly(ctx: Ctx, pts: [number, number][]) {
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
  ctx.closePath();
}

/** Item glyph centred on (x, y) with radius r. */
export function drawItem(ctx: Ctx, kind: ItemKind, x: number, y: number, r: number, corrupt: boolean, t: number) {
  const def = ITEMS[kind];
  let col = def.color;
  if (corrupt) {
    col = '#FF3DCE';
    x += Math.sin(t * 53 + y) * r * 0.25;
    y += Math.cos(t * 47 + x) * r * 0.25;
  }
  ctx.fillStyle = col;
  switch (def.glyph) {
    case 'circle':
      ctx.beginPath();
      ctx.arc(x, y, r * 0.8, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'diamond':
      poly(ctx, [[x, y - r], [x + r * 0.85, y], [x, y + r], [x - r * 0.85, y]]);
      ctx.fill();
      break;
    case 'square':
      roundRect(ctx, x - r * 0.75, y - r * 0.75, r * 1.5, r * 1.5, r * 0.3);
      ctx.fill();
      ctx.fillStyle = 'rgba(10,14,19,0.55)';
      ctx.fillRect(x - r * 0.42, y - r * 0.3, r * 0.84, r * 0.14);
      ctx.fillRect(x - r * 0.42, y + r * 0.05, r * 0.6, r * 0.14);
      break;
    case 'hex': {
      const pts: [number, number][] = [];
      for (let i = 0; i < 6; i++) pts.push([x + Math.cos((i * Math.PI) / 3) * r * 0.9, y + Math.sin((i * Math.PI) / 3) * r * 0.9]);
      poly(ctx, pts);
      ctx.fill();
      break;
    }
    case 'star': {
      const pts: [number, number][] = [];
      for (let i = 0; i < 10; i++) {
        const rad = i % 2 ? r * 0.45 : r * 0.95;
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        pts.push([x + Math.cos(a) * rad, y + Math.sin(a) * rad]);
      }
      poly(ctx, pts);
      ctx.fill();
      break;
    }
    case 'tri':
      poly(ctx, [[x, y - r * 0.9], [x + r * 0.9, y + r * 0.75], [x - r * 0.9, y + r * 0.75]]);
      ctx.fill();
      break;
  }
  if (corrupt) {
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(x - r * 0.9 + ((t * 40) % 1) * r, y - r * 0.15, r * 0.35, r * 0.18);
  }
}

/** Building icon inside a box of size s (pixels) centred on (cx, cy). */
export function drawIcon(ctx: Ctx, kind: BuildingKind, cx: number, cy: number, s: number, t: number, active: boolean) {
  const u = s / 2;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.lineWidth = Math.max(1.2, s * 0.045);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = INK;
  ctx.fillStyle = INK;
  switch (kind) {
    case 'indexer': {
      // A drill head that spins while working.
      const a = active ? t * 4 : 0;
      ctx.beginPath();
      ctx.arc(0, 0, u * 0.42, 0, Math.PI * 2);
      ctx.stroke();
      for (let i = 0; i < 3; i++) {
        const ang = a + (i * Math.PI * 2) / 3;
        ctx.beginPath();
        ctx.moveTo(Math.cos(ang) * u * 0.12, Math.sin(ang) * u * 0.12);
        ctx.lineTo(Math.cos(ang) * u * 0.38, Math.sin(ang) * u * 0.38);
        ctx.stroke();
      }
      break;
    }
    case 'specdesk': {
      roundRect(ctx, -u * 0.32, -u * 0.42, u * 0.64, u * 0.84, u * 0.08);
      ctx.stroke();
      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        ctx.moveTo(-u * 0.18, -u * 0.18 + i * u * 0.18);
        ctx.lineTo(u * (i === 2 ? 0.05 : 0.18), -u * 0.18 + i * u * 0.18);
        ctx.stroke();
      }
      break;
    }
    case 'forge': {
      // An agent: a soft hexagon with a spark that pulses while drafting.
      const pts: [number, number][] = [];
      for (let i = 0; i < 6; i++) pts.push([Math.cos((i * Math.PI) / 3 + Math.PI / 6) * u * 0.45, Math.sin((i * Math.PI) / 3 + Math.PI / 6) * u * 0.45]);
      poly(ctx, pts);
      ctx.stroke();
      const p = active ? 0.7 + Math.sin(t * 10) * 0.3 : 0.5;
      ctx.fillStyle = `rgba(201,168,255,${p})`;
      const sp: [number, number][] = [];
      for (let i = 0; i < 8; i++) {
        const rad = i % 2 ? u * 0.07 : u * 0.22;
        const ang = (i * Math.PI) / 4;
        sp.push([Math.cos(ang) * rad, Math.sin(ang) * rad]);
      }
      poly(ctx, sp);
      ctx.fill();
      break;
    }
    case 'bench': {
      ctx.beginPath();
      ctx.moveTo(0, -u * 0.45);
      ctx.lineTo(u * 0.36, -u * 0.3);
      ctx.lineTo(u * 0.32, u * 0.12);
      ctx.quadraticCurveTo(u * 0.2, u * 0.38, 0, u * 0.46);
      ctx.quadraticCurveTo(-u * 0.2, u * 0.38, -u * 0.32, u * 0.12);
      ctx.lineTo(-u * 0.36, -u * 0.3);
      ctx.closePath();
      ctx.stroke();
      ctx.strokeStyle = '#7CF0B0';
      ctx.beginPath();
      ctx.moveTo(-u * 0.16, 0);
      ctx.lineTo(-u * 0.03, u * 0.14);
      ctx.lineTo(u * 0.18, -u * 0.12);
      ctx.stroke();
      break;
    }
    case 'postmortem': {
      ctx.beginPath();
      ctx.arc(-u * 0.06, -u * 0.06, u * 0.26, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(u * 0.13, u * 0.13);
      ctx.lineTo(u * 0.38, u * 0.38);
      ctx.stroke();
      break;
    }
    case 'router': {
      for (let i = 0; i < 4; i++) {
        ctx.save();
        ctx.rotate((i * Math.PI) / 2);
        ctx.beginPath();
        ctx.moveTo(u * 0.15, 0);
        ctx.lineTo(u * 0.6, 0);
        ctx.moveTo(u * 0.45, -u * 0.13);
        ctx.lineTo(u * 0.6, 0);
        ctx.lineTo(u * 0.45, u * 0.13);
        ctx.stroke();
        ctx.restore();
      }
      break;
    }
    case 'rack': {
      for (let i = 0; i < 3; i++) {
        roundRect(ctx, -u * 0.38, -u * 0.4 + i * u * 0.28, u * 0.76, u * 0.2, u * 0.05);
        ctx.stroke();
        const on = Math.sin(t * 6 + i * 2.1) > -0.3;
        ctx.fillStyle = on ? '#F2D27A' : '#5A4A20';
        ctx.fillRect(u * 0.18, -u * 0.34 + i * u * 0.28, u * 0.1, u * 0.08);
      }
      break;
    }
    case 'breaker': {
      ctx.fillStyle = '#F2D27A';
      poly(ctx, [[u * 0.08, -u * 0.55], [-u * 0.28, u * 0.08], [-u * 0.02, u * 0.08], [-u * 0.12, u * 0.55], [u * 0.3, -u * 0.12], [u * 0.04, -u * 0.12]]);
      ctx.fill();
      break;
    }
    case 'monitor': {
      ctx.beginPath();
      ctx.arc(0, 0, u * 0.38, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, 0, u * 0.14, 0, Math.PI * 2);
      ctx.fillStyle = active ? '#7FE3FF' : DIM;
      ctx.fill();
      const a = t * 2.4;
      ctx.strokeStyle = 'rgba(127,227,255,0.8)';
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(Math.cos(a) * u * 0.38, Math.sin(a) * u * 0.38);
      ctx.stroke();
      break;
    }
    case 'firewall': {
      ctx.strokeStyle = '#B9C3CF';
      for (let r = 0; r < 3; r++) {
        const y = -u * 0.45 + r * u * 0.3;
        ctx.strokeRect(-u * 0.6, y, u * 1.2, u * 0.3);
        const off = r % 2 ? u * 0.3 : 0;
        ctx.beginPath();
        ctx.moveTo(-u * 0.3 + off, y);
        ctx.lineTo(-u * 0.3 + off, y + u * 0.3);
        ctx.moveTo(u * 0.3 + off - (r % 2 ? u * 0.6 : 0), y);
        ctx.lineTo(u * 0.3 + off - (r % 2 ? u * 0.6 : 0), y + u * 0.3);
        ctx.stroke();
      }
      break;
    }
    case 'checkpoint': {
      ctx.strokeStyle = '#7FD6FF';
      ctx.beginPath();
      ctx.arc(0, 0, u * 0.36, -Math.PI * 0.85, Math.PI * 0.6);
      ctx.stroke();
      const ex = Math.cos(Math.PI * 0.6) * u * 0.36;
      const ey = Math.sin(Math.PI * 0.6) * u * 0.36;
      ctx.beginPath();
      ctx.moveTo(ex - u * 0.14, ey - u * 0.02);
      ctx.lineTo(ex, ey);
      ctx.lineTo(ex + u * 0.03, ey - u * 0.15);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, 0, u * 0.08, 0, Math.PI * 2);
      ctx.fillStyle = '#7FD6FF';
      ctx.fill();
      break;
    }
    case 'scanner': {
      const a = t * 1.6;
      ctx.beginPath();
      ctx.arc(0, 0, u * 0.42, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, 0, u * 0.22, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = 'rgba(160,180,255,0.35)';
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, u * 0.42, a, a + 0.9);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'drones': {
      const pts: [number, number][] = [];
      for (let i = 0; i < 6; i++) pts.push([Math.cos((i * Math.PI) / 3) * u * 0.42, Math.sin((i * Math.PI) / 3) * u * 0.42]);
      poly(ctx, pts);
      ctx.stroke();
      for (let i = 0; i < 3; i++) {
        const ang = t * 2 + (i * Math.PI * 2) / 3;
        ctx.beginPath();
        ctx.arc(Math.cos(ang) * u * 0.2, Math.sin(ang) * u * 0.2, u * 0.07, 0, Math.PI * 2);
        ctx.fillStyle = '#D6B8FF';
        ctx.fill();
      }
      break;
    }
    case 'users': {
      ctx.fillStyle = '#9CF2C0';
      for (let i = -1; i <= 1; i++) {
        const x = i * u * 0.34;
        const y = i === 0 ? -u * 0.06 : u * 0.04;
        ctx.beginPath();
        ctx.arc(x, y - u * 0.16, u * 0.11, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x, y + u * 0.18, u * 0.19, Math.PI, 0);
        ctx.fill();
      }
      break;
    }
    case 'terminal': {
      roundRect(ctx, -u * 0.45, -u * 0.32, u * 0.9, u * 0.6, u * 0.08);
      ctx.stroke();
      ctx.font = `600 ${Math.round(u * 0.32)}px ui-monospace, Menlo, monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = Math.sin(t * 5) > 0 ? '#9CF2C0' : '#3E6B52';
      ctx.fillText('>_', 0, -u * 0.02);
      break;
    }
    case 'monolith': {
      ctx.strokeStyle = 'rgba(200,180,255,0.35)';
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        const y = -u * 0.5 + i * u * 0.25;
        ctx.moveTo(-u * 0.5, y);
        ctx.lineTo(u * 0.5, y + Math.sin(i * 7.3) * u * 0.05);
        ctx.stroke();
      }
      ctx.fillStyle = 'rgba(220,200,255,0.5)';
      ctx.font = `500 ${Math.round(u * 0.16)}px ui-monospace, Menlo, monospace`;
      ctx.textAlign = 'center';
      ctx.fillText('do not touch', 0, u * 0.62);
      break;
    }
    case 'nest': {
      const pulse = 1 + Math.sin(t * 3) * 0.08;
      for (let i = 0; i < 7; i++) {
        const ang = (i * Math.PI * 2) / 7 + t * 0.4;
        const rr = u * 0.28 * pulse;
        ctx.beginPath();
        ctx.arc(Math.cos(ang) * rr, Math.sin(ang) * rr, u * 0.16, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,61,206,${0.35 + (i % 3) * 0.15})`;
        ctx.fill();
      }
      ctx.beginPath();
      ctx.arc(0, 0, u * 0.18 * pulse, 0, Math.PI * 2);
      ctx.fillStyle = '#FF3DCE';
      ctx.fill();
      break;
    }
    case 'cache': {
      ctx.fillStyle = '#F2D27A';
      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        ctx.ellipse(0, u * 0.2 - i * u * 0.18, u * 0.32, u * 0.11, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#8A6A1E';
        ctx.stroke();
      }
      break;
    }
    case 'gate': {
      // A version check: a tick inside a bracket.
      ctx.strokeStyle = '#7FE3E0';
      ctx.beginPath();
      ctx.moveTo(-u * 0.32, -u * 0.42);
      ctx.lineTo(-u * 0.46, -u * 0.42);
      ctx.lineTo(-u * 0.46, u * 0.42);
      ctx.lineTo(-u * 0.32, u * 0.42);
      ctx.moveTo(u * 0.32, -u * 0.42);
      ctx.lineTo(u * 0.46, -u * 0.42);
      ctx.lineTo(u * 0.46, u * 0.42);
      ctx.lineTo(u * 0.32, u * 0.42);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-u * 0.2, 0);
      ctx.lineTo(-u * 0.04, u * 0.17);
      ctx.lineTo(u * 0.24, -u * 0.17);
      ctx.stroke();
      break;
    }
    case 'belt':
      break;
  }
  ctx.restore();
}

/** A stable colour per customer record, so collisions are visible. */
export function recordColor(rec: number): string {
  return `hsl(${Math.round((rec * 137.508) % 360)}, 72%, 66%)`;
}
